/**
 * DataLineageCrosswalkNew.tsx  (lazy)
 *
 * Self-fetching, lazily-paginated canonical ↔ source crosswalk.
 *
 * What changed for lazy loading
 * ─────────────────────────────
 * • No longer receives the full `refinedDM`. Receives { appNm, entityId } and
 *   self-fetches ONLY the selected canonical entity via getLineageProfile,
 *   paginating each sub-entity with getLineagePage ("Load more").
 * • Source FLD_IDs are resolved SERVER-side: every lineage entry already carries
 *   a `resolved` block, so the client never walks source entities to build an
 *   index. (The old buildSourceFieldIndex is gone.)
 * • Entity-wide stats come from the server (`stats`), so the coverage bar stays
 *   accurate even while pages are still loading.
 * • The Add-mapping autocomplete queries searchLineageSourceFields (debounced)
 *   instead of holding every source field in memory.
 *
 * The top EntityNavBar lives in MyComponent (it already owns navEntities +
 * selectEntity); MyComponent renders it above this view and passes the selected
 * entityId down — exactly like the "businessEntity" tab does for DataModelTree.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faFilter, faChevronRight, faChevronDown,
  faLayerGroup, faCircleInfo, faMagnifyingGlassChart, faPlus,
  faFloppyDisk, faXmark, faTableColumns, faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { T, injectTheme } from '../theme/theme';
import {
  getLineageProfile, getLineagePage, searchLineageSourceFields,
  type LineageField, type LineageEntry, type LineageSourceMeta,
  type LineageStats, type LineageSubEntity,
} from '../mdmAPI';
import {
 LAZY_CONFIG,
} from '../API';

// ─── Types ────────────────────────────────────────────────────────────────────
type SourceLineage = LineageEntry;
type CanonicalField = LineageField;

interface Props {
  appNm:           string;
  entityId:        string | null;
  entityLabel?:    string;
  onLineageSaved?: (canonicalFldId: string, lineage: SourceLineage[]) => void;
}

// Per-sub-entity client state (loaded fields + pagination meta)
interface SubState {
  name:        string;
  label:       string;
  description?: string;
  isRoot:      boolean;
  total:       number;
  loaded:      number;
  hasMore:     boolean;
  loading:     boolean;
  fields:      CanonicalField[];
}

// ─── Color / badge helpers (unchanged) ────────────────────────────────────────
function confColor(c: number): string {
  if (c >= 0.95) return T.green;
  if (c >= 0.80) return T.teal;
  if (c >= 0.60) return T.amber;
  return T.red;
}
function confBg(c: number): string {
  if (c >= 0.95) return T.greenBg;
  if (c >= 0.80) return T.tealBg;
  if (c >= 0.60) return T.amberBg;
  return T.redBg;
}
function confBorder(c: number): string {
  if (c >= 0.95) return T.greenBorder;
  if (c >= 0.80) return T.tealBorder;
  if (c >= 0.60) return T.amberBorder;
  return T.redBorder;
}
function confLabel(c: number): string {
  if (c >= 0.95) return 'Exact';
  if (c >= 0.80) return 'Strong';
  if (c >= 0.60) return 'Likely';
  return 'Possible';
}
const MATCH_STYLE: Record<string, { bg: string; text: string; border: string }> = {
  EXACT:           { bg: T.greenBg,  text: T.green,  border: T.greenBorder },
  SEMANTIC:        { bg: T.blueBg,   text: T.blue,   border: T.blueBorder  },
  STRUCTURAL:      { bg: T.purpleBg, text: T.purple, border: T.purpleBorder },
  DUPLICATE_TABLE: { bg: T.amberBg,  text: T.amber,  border: T.amberBorder },
};
const MATCH_STYLE_DEFAULT = { bg: T.slateBg, text: T.slate, border: T.slateBorder };
function matchStyle(mt: string) { return MATCH_STYLE[mt] || MATCH_STYLE_DEFAULT; }
const SYS_STYLE: Record<string, { bg: string; text: string }> = {
  SAP:    { bg: T.blueBg,   text: T.blue   },
  ORACLE: { bg: T.amberBg,  text: T.amber  },
  MDM:    { bg: T.greenBg,  text: T.green  },
  JDE:    { bg: T.purpleBg, text: T.purple },
};
const SYS_STYLE_DEFAULT = { bg: T.slateBg, text: T.slate };
function sysStyle(s: string) { return SYS_STYLE[(s || '').toUpperCase()] || SYS_STYLE_DEFAULT; }
function typeShort(t: string): string { return (t || '').replace('Field', '') || '?'; }

// ─── Pill ─────────────────────────────────────────────────────────────────────
const Pill: React.FC<{ label: string; bg: string; text: string; border: string; size?: number }> =
  ({ label, bg, text, border, size = 10 }) => (
  <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 20, fontSize: size,
    fontWeight: 700, background: bg, color: text, border: `1px solid ${border}`,
    whiteSpace: 'nowrap', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.03em' }}>
    {label}
  </span>
);

// ─── Coverage bar (now fed by server stats — accurate during pagination) ──────
const CoverageBar: React.FC<{ stats: LineageStats; sourceSystems: string[] }> = ({ stats, sourceSystems }) => {
  const pct = stats.total_fields > 0 ? Math.round((stats.cross_system_fields / stats.total_fields) * 100) : 0;
  const cards = [
    { val: stats.total_fields,        sub: 'canonical fields',    color: T.brand },
    { val: sourceSystems.length,      sub: 'source systems',      color: T.blue },
    { val: stats.cross_system_fields, sub: 'cross-system mapped', color: T.green },
    { val: stats.unmapped_fields,     sub: 'unmapped',            color: stats.unmapped_fields > 0 ? T.amber : T.textMuted },
    { val: `${Math.round(stats.avg_confidence * 100)}%`, sub: 'avg confidence', color: confColor(stats.avg_confidence) },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px', background: T.bgCard,
      border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 12, flexWrap: 'wrap' }}>
      {cards.map(s => (
        <div key={s.sub} style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
          <span style={{ fontSize: 20, fontWeight: 700, color: s.color, fontFamily: "'JetBrains Mono', monospace" }}>{s.val}</span>
          <span style={{ fontSize: 11, color: T.textMuted }}>{s.sub}</span>
        </div>
      ))}
      <div style={{ flex: 1, minWidth: 120 }}>
        <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 4 }}>Cross-system coverage {pct}%</div>
        <div style={{ height: 5, borderRadius: 3, background: T.border, overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 3, width: `${pct}%`,
            background: pct >= 80 ? T.green : pct >= 50 ? T.amber : T.red, transition: 'width 0.4s ease' }} />
        </div>
      </div>
    </div>
  );
};

// ─── Lineage mapping card (uses server-resolved meta) ─────────────────────────
interface LineageCardProps {
  lineage:  SourceLineage;
  index:    number;
  onUpdate: (li: number, patch: Partial<SourceLineage>) => void;
  onDelete: (li: number) => void;
}
const LineageCard: React.FC<LineageCardProps> = ({ lineage, index, onUpdate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [draftConf, setDraftConf] = useState<number>(Math.round((lineage._sme_conf ?? lineage.confidence) * 100));
  const [draftMatch, setDraftMatch] = useState<string>(lineage._sme_match ?? lineage.match_type);

  const meta: LineageSourceMeta | null | undefined = lineage.resolved;
  const effectiveConf  = (lineage._sme_conf  ?? lineage.confidence);
  const effectiveMatch = (lineage._sme_match ?? lineage.match_type);
  const isSmeEdited = lineage._sme_conf != null || lineage._sme_match != null;
  const ms = matchStyle(effectiveMatch);
  const ss = sysStyle(lineage.dataSrcSystemId);

  const displayName = meta ? meta.COLUMN_DISPLAY_NAME : (lineage.FLD_ID.split('-').pop() || lineage.FLD_ID);
  const subLabel = meta && (meta.ORIG_TABLE_NAME || meta.ORIG_COLUMN_NAME)
    ? `${meta.ORIG_TABLE_NAME} | ${meta.ORIG_COLUMN_NAME}` : lineage.FLD_ID;

  const handleSave = () => { onUpdate(index, { _sme_conf: draftConf / 100, _sme_match: draftMatch }); setOpen(false); };
  const handleReset = () => { onUpdate(index, { _sme_conf: null, _sme_match: null });
    setDraftConf(Math.round(lineage.confidence * 100)); setDraftMatch(lineage.match_type); };

  return (
    <div style={{ border: `1px solid ${open ? T.brandMid : T.border}`, borderRadius: 8, background: T.bgCard,
      overflow: 'hidden', transition: 'border-color 0.15s' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto auto auto', gap: 10, alignItems: 'center', padding: '8px 12px',
        cursor: 'pointer', background: open ? T.brandLight : 'transparent', transition: 'background 0.12s' }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = T.bgSidebar; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'transparent'; }}>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: ss.bg,
          color: ss.text, flexShrink: 0, fontFamily: "'JetBrains Mono', monospace" }}>{lineage.dataSrcSystemId}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.textPrimary, overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={displayName}>{displayName}</div>
          <div style={{ fontSize: 10, color: T.textMuted, fontFamily: "'JetBrains Mono', monospace",
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={subLabel}>{subLabel}</div>
        </div>
        <span style={{ fontSize: 10, color: T.textMuted, fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>
          {lineage.entityId}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 80, flexShrink: 0 }}>
          <div style={{ flex: 1, height: 4, background: T.border, borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ width: `${Math.round(effectiveConf * 100)}%`, height: '100%',
              background: confColor(effectiveConf), borderRadius: 2, transition: 'width 0.3s' }} />
          </div>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700,
            color: confColor(effectiveConf), minWidth: 28, textAlign: 'right' }}>
            {Math.round(effectiveConf * 100)}%</span>
        </div>
        <Pill label={effectiveMatch.replace('_', ' ')} bg={ms.bg} text={ms.text} border={ms.border} />
        {isSmeEdited && (
          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 6, background: T.purpleBg,
            color: T.purple, border: `1px solid ${T.purpleBorder}`, fontFamily: "'JetBrains Mono', monospace" }}>SME</span>
        )}
        <button onClick={e => { e.stopPropagation(); onDelete(index); }} style={{ width: 22, height: 22,
          border: 'none', background: 'none', cursor: 'pointer', color: T.textMuted, borderRadius: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} title="Remove this mapping"
          onMouseEnter={e => (e.currentTarget.style.background = T.redBg)}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
          <FontAwesomeIcon icon={faXmark} style={{ fontSize: 10, color: T.red }} />
        </button>
      </div>
      {open && (
        <div style={{ borderTop: `1px solid ${T.border}`, padding: '12px 14px', background: T.bgSidebar,
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="gw-label">Source field details</div>
            {[
              { lbl: 'Display name', val: meta?.COLUMN_DISPLAY_NAME || '—' },
              { lbl: 'Source table',  val: meta?.ORIG_TABLE_NAME    || '—' },
              { lbl: 'Source column', val: meta?.ORIG_COLUMN_NAME   || '—' },
              { lbl: 'Data type',     val: meta?.DATA_TYP            || '—' },
              { lbl: 'Description',   val: meta?.COLUMN_SHORT_DESC  || '—' },
              { lbl: 'FLD_ID (raw)',  val: lineage.FLD_ID },
              { lbl: 'Entity ID',     val: lineage.entityId },
              { lbl: 'Original conf', val: `${Math.round(lineage.confidence * 100)}% — ${confLabel(lineage.confidence)}` },
            ].map(({ lbl, val }) => (
              <div key={lbl} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
                  color: T.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>{lbl}</span>
                <span style={{ fontSize: 12, color: T.textPrimary,
                  fontFamily: ['FLD_ID (raw)', 'Entity ID', 'Source table', 'Source column'].includes(lbl)
                    ? "'JetBrains Mono', monospace" : 'inherit' }}>{val}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="gw-label">SME override</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
                color: T.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>Confidence: {draftConf}%</span>
              <input type="range" min={0} max={100} step={1} value={draftConf}
                onChange={e => setDraftConf(Number(e.target.value))} style={{ width: '100%' }} />
              <span style={{ fontSize: 11, color: confColor(draftConf / 100), fontWeight: 700 }}>
                {confLabel(draftConf / 100)} · {draftConf}%</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
                color: T.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>Match type</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {(['EXACT', 'SEMANTIC', 'STRUCTURAL', 'DUPLICATE_TABLE'] as const).map(mt => {
                  const mst = matchStyle(mt); const active = draftMatch === mt;
                  return (
                    <button key={mt} onClick={() => setDraftMatch(mt)} style={{ fontSize: 10, fontWeight: 700,
                      padding: '3px 9px', borderRadius: 9, cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace",
                      background: active ? mst.text : mst.bg, color: active ? '#fff' : mst.text,
                      border: `1px solid ${mst.border}`, transition: 'all 0.12s' }}>{mt.replace('_', ' ')}</button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
              <button onClick={handleSave} className="gw-btn gw-btn-sm gw-btn-primary"
                style={{ flex: 1, justifyContent: 'center' }}>
                <FontAwesomeIcon icon={faFloppyDisk} /> Save changes</button>
              {isSmeEdited && (
                <button onClick={handleReset} className="gw-btn gw-btn-sm gw-btn-ghost" title="Reset to LLM original">Reset</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Add mapping form (autocomplete via server search) ────────────────────────
interface AddMappingFormProps {
  appNm:    string;
  onAdd:    (l: SourceLineage) => void;
  onCancel: () => void;
}
const AddMappingForm: React.FC<AddMappingFormProps> = ({ appNm, onAdd, onCancel }) => {
  const [sys, setSys] = useState('SAP');
  const [entity, setEntity] = useState('');
  const [fldId, setFldId] = useState('');
  const [match, setMatch] = useState<string>('SEMANTIC');
  const [conf, setConf] = useState(80);
  const [hint, setHint] = useState<(LineageSourceMeta & { FLD_ID: string }) | null>(null);
  const [results, setResults] = useState<Array<LineageSourceMeta & { FLD_ID: string }>>([]);
  const debounceRef = useRef<any>(null);

  // Debounced server search — never holds the full source field set client-side.
  useEffect(() => {
    if (!fldId.trim()) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchLineageSourceFields(appNm, fldId.trim(), 8)
        .then(res => setResults(res?.results || []))
        .catch(() => setResults([]));
    }, 220);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [fldId, appNm]);

  const pick = (m: LineageSourceMeta & { FLD_ID: string }) => {
    setFldId(m.FLD_ID); setHint(m); setResults([]);
    if (m.dataSrcSystemId) setSys(m.dataSrcSystemId);
    if (m.entityId) setEntity(m.entityId);
  };

  const handleAdd = () => {
    if (!fldId.trim() || !entity.trim()) return;
    onAdd({ entityId: entity.trim(), dataSrcSystemId: sys, FLD_ID: fldId.trim(),
      confidence: conf / 100, match_type: match, resolved: hint || null });
  };

  return (
    <div style={{ border: `1px dashed ${T.brandMid}`, borderRadius: 8, padding: 14, background: T.brandLight, marginTop: 6 }}>
      <div className="gw-label" style={{ marginBottom: 10 }}>Add source mapping</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label className="gw-label">Source system</label>
          <select value={sys} onChange={e => setSys(e.target.value)} className="gw-select"
            style={{ fontSize: 12, height: 30, padding: '0 28px 0 8px' }}>
            <option value="SAP">SAP</option><option value="ORACLE">ORACLE</option>
            <option value="MDM">MDM</option><option value="JDE">JDE</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label className="gw-label">Entity ID</label>
          <input value={entity} onChange={e => setEntity(e.target.value)} placeholder="e.g. NAITGLDA"
            className="gw-input" style={{ fontSize: 12, height: 30, padding: '0 8px' }} />
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10, position: 'relative' }}>
        <label className="gw-label">Source FLD_ID</label>
        <input value={fldId} onChange={e => { setFldId(e.target.value); setHint(null); }}
          placeholder="Start typing e.g. SapSupplierMaster-…" className="gw-input"
          style={{ fontSize: 12, height: 30, padding: '0 8px', fontFamily: "'JetBrains Mono', monospace" }} />
        {results.length > 0 && !hint && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: T.bgCard,
            border: `1px solid ${T.border}`, borderRadius: 6, zIndex: 50, maxHeight: 180, overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            {results.map(m => (
              <button key={m.FLD_ID} onClick={() => pick(m)} style={{ display: 'block', width: '100%',
                textAlign: 'left', padding: '7px 10px', border: 'none', background: 'none', cursor: 'pointer',
                borderBottom: `1px solid ${T.border}`, fontFamily: 'inherit' }}
                onMouseEnter={e => (e.currentTarget.style.background = T.brandLight)}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.textPrimary, fontFamily: "'JetBrains Mono', monospace" }}>{m.FLD_ID}</div>
                <div style={{ fontSize: 10, color: T.textMuted }}>{m.COLUMN_DISPLAY_NAME} · {m.ORIG_TABLE_NAME}</div>
              </button>
            ))}
          </div>
        )}
        {hint && (
          <div style={{ fontSize: 11, color: T.green, marginTop: 2, display: 'flex', gap: 6, alignItems: 'center' }}>
            <FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: 10 }} />
            Resolved: <strong>{hint.COLUMN_DISPLAY_NAME}</strong> ({hint.ORIG_TABLE_NAME} | {hint.ORIG_COLUMN_NAME})
          </div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label className="gw-label">Match type</label>
          <select value={match} onChange={e => setMatch(e.target.value)} className="gw-select"
            style={{ fontSize: 12, height: 30, padding: '0 28px 0 8px' }}>
            <option value="EXACT">EXACT</option><option value="SEMANTIC">SEMANTIC</option>
            <option value="STRUCTURAL">STRUCTURAL</option><option value="DUPLICATE_TABLE">DUPLICATE_TABLE</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label className="gw-label">Confidence: {conf}%</label>
          <input type="range" min={0} max={100} step={1} value={conf}
            onChange={e => setConf(Number(e.target.value))} style={{ marginTop: 4 }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} className="gw-btn gw-btn-sm gw-btn-ghost">Cancel</button>
        <button onClick={handleAdd} className="gw-btn gw-btn-sm gw-btn-primary"
          disabled={!fldId.trim() || !entity.trim()}>
          <FontAwesomeIcon icon={faPlus} /> Add mapping</button>
      </div>
    </div>
  );
};

// ─── Single canonical field row ────────────────────────────────────────────────
interface FieldRowProps {
  field: CanonicalField; index: number; appNm: string; filterSys: string;
  onSaveField: (fldId: string, lineage: SourceLineage[]) => void;
}
const FieldRow: React.FC<FieldRowProps> = ({ field, index, appNm, filterSys, onSaveField }) => {
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [lineage, setLineage] = useState<SourceLineage[]>(() => JSON.parse(JSON.stringify(field.source_lineage || [])));
  const [isDirty, setIsDirty] = useState(false);

  const visibleLineage = filterSys === '__ALL__' ? lineage : lineage.filter(l => l.dataSrcSystemId === filterSys);
  if (visibleLineage.length === 0 && filterSys !== '__ALL__') return null;

  const systems = [...new Set(lineage.map(l => l.dataSrcSystemId))];
  const maxConf = lineage.length > 0 ? Math.max(...lineage.map(l => l._sme_conf ?? l.confidence)) : 0;
  const isOdd = index % 2 === 0;

  const handleUpdate = (li: number, patch: Partial<SourceLineage>) => {
    setLineage(prev => prev.map((l, i) => i === li ? { ...l, ...patch } : l)); setIsDirty(true);
  };
  const handleDelete = (li: number) => { setLineage(prev => prev.filter((_, i) => i !== li)); setIsDirty(true); };
  const handleAdd = (l: SourceLineage) => { setLineage(prev => [...prev, l]); setAdding(false); setIsDirty(true); };
  const handleSave = () => { onSaveField(field.FLD_ID, lineage); setIsDirty(false); };

  return (
    <>
      <tr style={{ background: isOdd ? T.bgCard : T.bgSidebar, cursor: 'pointer', borderBottom: `1px solid ${T.border}` }}
        onClick={() => setExpanded(e => !e)}>
        <td style={{ width: 32, padding: '9px 0 9px 12px', verticalAlign: 'middle' }}>
          <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} style={{ fontSize: 10, color: T.textMuted }} />
        </td>
        <td style={{ padding: '9px 12px', verticalAlign: 'middle', minWidth: 200 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.brand, marginBottom: 2 }}>
            {field.MANDATORY === 'Y' && (
              <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: T.red,
                marginRight: 5, verticalAlign: 'middle', flexShrink: 0 }} />
            )}
            {field.COLUMN_DISPLAY_NAME}
          </div>
          <div style={{ fontSize: 10, color: T.textMuted, fontFamily: "'JetBrains Mono', monospace" }}>{field.COLUMN_NAME}</div>
        </td>
        <td style={{ padding: '9px 12px', verticalAlign: 'middle' }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: T.bgSidebar,
            color: T.textMuted, border: `1px solid ${T.border}`, fontFamily: "'JetBrains Mono', monospace" }}>
            {typeShort(field.DATA_TYP)}</span>
        </td>
        <td style={{ padding: '9px 12px', verticalAlign: 'middle' }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            {systems.map(s => { const ss = sysStyle(s); return (
              <span key={s} style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 4,
                background: ss.bg, color: ss.text, fontFamily: "'JetBrains Mono', monospace" }}>{s}</span>
            ); })}
            {systems.length === 0 && <span style={{ fontSize: 11, color: T.amber }}>Unmapped</span>}
            <span style={{ fontSize: 11, color: T.textMuted }}>{lineage.length} mapping{lineage.length !== 1 ? 's' : ''}</span>
          </div>
        </td>
        <td style={{ padding: '9px 12px', verticalAlign: 'middle' }}>
          {lineage.length > 0
            ? <Pill label={`${confLabel(maxConf)} · ${Math.round(maxConf * 100)}%`} bg={confBg(maxConf)} text={confColor(maxConf)} border={confBorder(maxConf)} />
            : <span style={{ fontSize: 11, color: T.textMuted }}>—</span>}
        </td>
        <td style={{ padding: '9px 12px', verticalAlign: 'middle', maxWidth: 200 }}>
          <span style={{ fontSize: 11, color: T.textMuted, display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {field.COLUMN_SHORT_DESC || field.COLUMN_LONG_DESC || '—'}</span>
        </td>
        <td style={{ padding: '9px 12px', verticalAlign: 'middle', width: 80 }}>
          {isDirty && (
            <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: T.amberBg,
              color: T.amber, border: `1px solid ${T.amberBorder}`, fontFamily: "'JetBrains Mono', monospace" }}>Unsaved</span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={{ padding: 0, background: T.bgSidebar }}>
            <div style={{ padding: '14px 20px 16px 48px', borderBottom: `1px solid ${T.border}` }}>
              {(field.COLUMN_LONG_DESC || field.COLUMN_SHORT_DESC) && (
                <div style={{ fontSize: 12, color: T.textSecond, lineHeight: 1.6, padding: '10px 14px',
                  marginBottom: 14, background: T.bgCard, borderRadius: 8, borderLeft: `2px solid ${T.brandMid}` }}>
                  {field.COLUMN_LONG_DESC || field.COLUMN_SHORT_DESC}</div>
              )}
              <div className="gw-label" style={{ marginBottom: 8 }}>
                Source mappings
                {filterSys !== '__ALL__' && (
                  <span style={{ marginLeft: 8, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(filtered to {filterSys})</span>
                )}
              </div>
              {visibleLineage.length === 0 ? (
                <div style={{ padding: '20px 0', textAlign: 'center', color: T.textMuted, fontSize: 12 }}>
                  No mappings for the selected source system.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 10 }}>
                  {visibleLineage.map((l) => (
                    <LineageCard key={`${l.FLD_ID}-${lineage.indexOf(l)}`} lineage={l} index={lineage.indexOf(l)}
                      onUpdate={handleUpdate} onDelete={handleDelete} />
                  ))}
                </div>
              )}
              {adding ? (
                <AddMappingForm appNm={appNm} onAdd={handleAdd} onCancel={() => setAdding(false)} />
              ) : (
                <button onClick={() => setAdding(true)} className="gw-btn gw-btn-sm gw-btn-outline"
                  style={{ width: '100%', justifyContent: 'center' }}>
                  <FontAwesomeIcon icon={faPlus} /> Add source mapping</button>
              )}
              {isDirty && (
                <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: T.amber }}>Unsaved changes to this field's lineage</span>
                  <button onClick={handleSave} className="gw-btn gw-btn-sm gw-btn-success">
                    <FontAwesomeIcon icon={faFloppyDisk} /> Save lineage</button>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Sub-entity section (collapsible group, with "Load more") ─────────────────
interface SubEntitySectionProps {
  sub: SubState; defaultOpen: boolean; appNm: string; filterSys: string; searchQuery: string;
  onLoadMore: (subName: string) => void;
  onSaveField: (fldId: string, lineage: SourceLineage[]) => void;
}
const SubEntitySection: React.FC<SubEntitySectionProps> = ({
  sub, defaultOpen, appNm, filterSys, searchQuery, onLoadMore, onSaveField,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const visibleFields = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return sub.fields.filter(f => {
      if (filterSys !== '__ALL__' && !f.source_lineage.some(l => l.dataSrcSystemId === filterSys)) return false;
      if (!q) return true;
      return (
        f.COLUMN_NAME.toLowerCase().includes(q) ||
        (f.COLUMN_DISPLAY_NAME || '').toLowerCase().includes(q) ||
        (f.COLUMN_SHORT_DESC || '').toLowerCase().includes(q) ||
        f.source_lineage.some(l =>
          l.FLD_ID.toLowerCase().includes(q) ||
          l.dataSrcSystemId.toLowerCase().includes(q) ||
          (l.entityId || '').toLowerCase().includes(q) ||
          (l.resolved?.COLUMN_DISPLAY_NAME || '').toLowerCase().includes(q) ||
          (l.resolved?.ORIG_COLUMN_NAME || '').toLowerCase().includes(q)
        )
      );
    });
  }, [sub.fields, filterSys, searchQuery]);

  const multiCount = visibleFields.filter(f => new Set(f.source_lineage.map(l => l.dataSrcSystemId)).size >= 2).length;

  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 10, overflow: 'hidden', background: T.bgCard }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '11px 16px', background: open ? T.bgSidebar : T.bgCard, border: 'none', cursor: 'pointer',
        textAlign: 'left', borderBottom: open ? `1px solid ${T.border}` : 'none', transition: 'background 0.15s', fontFamily: 'inherit' }}>
        <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} style={{ fontSize: 11, color: T.textMuted, width: 12 }} />
        <FontAwesomeIcon icon={faTableColumns} style={{ fontSize: 12, color: T.brand }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: T.brand, flex: 1 }}>{sub.label}</span>
        {sub.description && (
          <span style={{ fontSize: 11, color: T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap', maxWidth: 280, flex: 2 }}>{sub.description}</span>
        )}
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: T.textMuted, flexShrink: 0 }}>
          {sub.loaded < sub.total ? `${sub.loaded}/${sub.total}` : sub.total} field{sub.total !== 1 ? 's' : ''} · {multiCount} cross-system
        </span>
        {sub.loading && <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 10, color: T.textMuted }} />}
      </button>
      {open && (
        visibleFields.length === 0 && sub.loaded > 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: T.textMuted, fontSize: 12 }}>
            No fields match the current filters.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.bgSidebar, borderBottom: `1px solid ${T.border}` }}>
                  <th style={{ width: 32, padding: '8px 0 8px 12px' }} />
                  {['Canonical Field', 'Type', 'Source Systems', 'Confidence', 'Description', ''].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 9, fontWeight: 700,
                      fontFamily: "'JetBrains Mono', monospace", letterSpacing: '.12em', textTransform: 'uppercase',
                      color: T.textMuted, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleFields.map((f, i) => (
                  <FieldRow key={f.FLD_ID || f.COLUMN_NAME} field={f} index={i} appNm={appNm}
                    filterSys={filterSys} onSaveField={onSaveField} />
                ))}
              </tbody>
            </table>
            {sub.hasMore && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
                <button onClick={() => onLoadMore(sub.name)} disabled={sub.loading}
                  className="gw-btn gw-btn-sm gw-btn-outline">
                  {sub.loading ? <><FontAwesomeIcon icon={faSpinner} spin /> Loading…</>
                    : `Load more fields — ${sub.loaded} of ${sub.total}`}
                </button>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────
const DataLineageCrosswalkView: React.FC<Props> = ({ appNm, entityId, entityLabel, onLineageSaved }) => {
  useEffect(() => { injectTheme(); }, []);

  const [loading, setLoading]   = useState(false);
  const [label, setLabel]       = useState(entityLabel || '');
  const [stats, setStats]       = useState<LineageStats | null>(null);
  const [systems, setSystems]   = useState<string[]>([]);
  const [subs, setSubs]         = useState<SubState[]>([]);
  const [search, setSearch]     = useState('');
  const [filterSys, setFilterSys] = useState('__ALL__');

  // Fetch the selected entity's crosswalk (first page of every sub-entity).
  useEffect(() => {
    if (!appNm || !entityId) { setSubs([]); setStats(null); return; }
    let cancelled = false;
    setLoading(true);
    setSubs([]); setStats(null); setSearch(''); setFilterSys('__ALL__');
    getLineageProfile(appNm, entityId, LAZY_CONFIG.PAGE_SIZE)
      .then(res => {
        if (cancelled) return;
        setLabel(res.entity_label || entityLabel || '');
        setStats(res.stats);
        setSystems(res.source_systems || []);
        setSubs((res.sub_entities || []).map((s: LineageSubEntity) => ({
          name: s.name, label: s.label, description: s.description, isRoot: s.is_root,
          total: s.total, loaded: s.loaded, hasMore: s.has_more, loading: false, fields: s.fields || [],
        })));
      })
      .catch(() => { if (!cancelled) { setSubs([]); setStats(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [appNm, entityId, entityLabel]);

  const loadMore = useCallback((subName: string) => {
    if (!appNm || !entityId) return;
    setSubs(prev => prev.map(s => s.name === subName ? { ...s, loading: true } : s));
    const cur = subs.find(s => s.name === subName);
    const offset = cur?.loaded ?? 0;
    getLineagePage(appNm, entityId, subName, offset, LAZY_CONFIG.PAGE_SIZE)
      .then(res => {
        setSubs(prev => prev.map(s => s.name === subName ? {
          ...s, fields: [...s.fields, ...(res.fields || [])],
          loaded: s.loaded + (res.fields?.length || 0), total: res.total, hasMore: res.has_more, loading: false,
        } : s));
      })
      .catch(() => setSubs(prev => prev.map(s => s.name === subName ? { ...s, loading: false } : s)));
  }, [appNm, entityId, subs]);

  const handleSaveField = useCallback((fldId: string, lineage: SourceLineage[]) => {
    onLineageSaved?.(fldId, lineage);
  }, [onLineageSaved]);

  if (!entityId) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>
        <div style={{ fontSize: 32, opacity: 0.3, marginBottom: 12 }}><FontAwesomeIcon icon={faMagnifyingGlassChart} /></div>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, marginBottom: 6 }}>Select an entity</div>
        <div style={{ fontSize: 13 }}>Choose a canonical entity from the bar above to view its lineage crosswalk.</div>
      </div>
    );
  }

  if (loading && !subs.length) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240, gap: 10, color: T.textMuted }}>
        <FontAwesomeIcon icon={faSpinner} spin /> Loading lineage crosswalk…
      </div>
    );
  }

  if (!loading && !subs.length) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>
        <div style={{ fontSize: 32, opacity: 0.3, marginBottom: 12 }}><FontAwesomeIcon icon={faMagnifyingGlassChart} /></div>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, marginBottom: 6 }}>No crosswalk for this entity</div>
        <div style={{ fontSize: 13 }}>This entity has no canonical lineage yet, or it isn't a canonical entity.</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 10px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0 14px' }}>
        <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: 14, color: T.purple }} />
        <span style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary }}>{label || 'Canonical Data Model'}</span>
        <span style={{ fontSize: 12, color: T.textMuted }}>· Lineage Crosswalk</span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, padding: '2px 8px', borderRadius: 8,
          background: T.purpleBg, color: T.purple, border: `1px solid ${T.purpleBorder}` }}>
          {subs.length} sub-entit{subs.length !== 1 ? 'ies' : 'y'}</span>
        {loading && <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 11, color: T.textMuted }} />}
      </div>

      {stats && <CoverageBar stats={stats} sourceSystems={systems} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
          <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: 10, top: '50%',
            transform: 'translateY(-50%)', fontSize: 11, color: T.textMuted, pointerEvents: 'none' }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search loaded fields, column names, source FLD_IDs…" className="gw-input"
            style={{ paddingLeft: 30, fontSize: 12 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FontAwesomeIcon icon={faFilter} style={{ fontSize: 10, color: T.textMuted }} />
          <select value={filterSys} onChange={e => setFilterSys(e.target.value)} className="gw-select"
            style={{ fontSize: 12, height: 36, padding: '0 28px 0 10px', minWidth: 130 }}>
            <option value="__ALL__">All systems</option>
            {systems.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {stats && <span style={{ fontSize: 11, color: T.textMuted, whiteSpace: 'nowrap' }}>{stats.total_fields} fields</span>}
      </div>

      {subs.map((sub, gi) => (
        <SubEntitySection key={sub.name} sub={sub} defaultOpen={gi === 0} appNm={appNm}
          filterSys={filterSys} searchQuery={search} onLoadMore={loadMore} onSaveField={handleSaveField} />
      ))}

      <div style={{ padding: '10px 16px', borderTop: `1px solid ${T.border}`, marginTop: 8, display: 'flex',
        alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span className="gw-label">Match type</span>
        {(['EXACT', 'SEMANTIC', 'STRUCTURAL', 'DUPLICATE_TABLE'] as const).map(mt => {
          const ms = matchStyle(mt);
          return <Pill key={mt} label={mt.replace('_', ' ')} bg={ms.bg} text={ms.text} border={ms.border} size={10} />;
        })}
        <span className="gw-label" style={{ marginLeft: 8 }}>Confidence</span>
        {[0.97, 0.85, 0.65, 0.45].map(c => (
          <Pill key={c} label={confLabel(c)} bg={confBg(c)} text={confColor(c)} border={confBorder(c)} size={10} />
        ))}
        <span style={{ fontSize: 11, color: T.textMuted, marginLeft: 4 }}>
          <FontAwesomeIcon icon={faCircleInfo} style={{ marginRight: 4 }} />
          Click a row to expand · SME-edited mappings show a purple badge
        </span>
      </div>
    </div>
  );
};

export default DataLineageCrosswalkView;